import { ChevronLeft, ChevronRight } from 'react-feather'
import styled from 'styled-components/macro'

import { ThemedText } from '../../theme/components/text'
import { ButtonOutlined } from '../Button'

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1.5rem;
  gap: 0.5rem;
`

const PageButton = styled(ButtonOutlined)<{ active?: boolean }>`
  padding: 0.25rem;
  font-size: 14px;
  height: 32px;
  min-width: 32px;
  color: ${({ theme, active }) => (active ? theme.white : theme.textPrimary)};
  background-color: ${({ theme, active }) => (active ? theme.accentAction : 'transparent')};
  border-color: ${({ theme, active }) => (active ? theme.accentAction : theme.backgroundOutline)};

  &:hover {
    color: ${({ theme, active }) => (active ? theme.white : theme.textPrimary)};
    background-color: ${({ theme, active }) => (active ? theme.accentActionSoft : theme.backgroundModule)};
    border-color: ${({ theme, active }) => (active ? theme.accentAction : theme.backgroundOutline)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const NavButton = styled(PageButton)`
  padding: 0.25rem;
  min-width: 32px;
  width: 32px;

  svg {
    width: 16px;
    height: 16px;
  }
`

const PageText = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const Ellipsis = styled(ThemedText.BodySmall)`
  color: ${({ theme }) => theme.textSecondary};
  width: 100px;
`

interface PaginationProps {
  currentPage: number
  pageCount: number
  onPageChange: (page: number) => void
  maxVisibleButtons?: number
}

/**
 * Advanced pagination component that shows page numbers with ellipsis
 *
 * Example displays:
 * < 1 2 "3" ... 7 8 >
 * < 1 2 ... "4" ... 7 8 >
 *
 * where "number" is the selected page
 */
export default function Pagination({ currentPage, pageCount, onPageChange, maxVisibleButtons = 7 }: PaginationProps) {
  // Handle prev/next page navigation
  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < pageCount) {
      onPageChange(currentPage + 1)
    }
  }

  // Get array of page numbers to display
  const getPageNumbers = () => {
    // If not many pages, show all
    if (pageCount <= maxVisibleButtons) {
      return Array.from({ length: pageCount }, (_, i) => i + 1)
    }

    const halfVisibleButtons = Math.floor(maxVisibleButtons / 2)
    const showLeftDots = currentPage > halfVisibleButtons + 1
    const showRightDots = currentPage < pageCount - halfVisibleButtons

    if (showLeftDots && showRightDots) {
      // Show both left and right dots (current page is in the middle)
      const leftSidePages = 2
      const rightSidePages = 2

      const leftPages = Array.from({ length: leftSidePages }, (_, i) => i + 1)
      const rightPages = Array.from({ length: rightSidePages }, (_, i) => pageCount - i).reverse()

      return [...leftPages, 'leftDots', currentPage, 'rightDots', ...rightPages]
    } else if (showLeftDots) {
      // Show only left dots (current page is closer to the end)
      const rightPageCount = maxVisibleButtons - 2 // -2 for left dots and first page
      const rightPages = Array.from({ length: rightPageCount }, (_, i) => pageCount - rightPageCount + i + 1)

      return [1, 'leftDots', ...rightPages]
    } else {
      // Show only right dots (current page is closer to the start)
      const leftPageCount = maxVisibleButtons - 2 // -2 for right dots and last page
      const leftPages = Array.from({ length: leftPageCount }, (_, i) => i + 1)

      return [...leftPages, 'rightDots', pageCount]
    }
  }

  const pageNumbers = getPageNumbers()

  return (
    <PaginationContainer>
      <NavButton onClick={handlePrevPage} disabled={currentPage <= 1}>
        <ChevronLeft />
      </NavButton>

      {pageNumbers.map((pageNumber, index) => {
        if (pageNumber === 'leftDots' || pageNumber === 'rightDots') {
          return <Ellipsis key={`dots-${index}`}>...</Ellipsis>
        }

        const pageNum = pageNumber as number
        return (
          <PageButton key={pageNum} active={currentPage === pageNum} onClick={() => onPageChange(pageNum)}>
            <PageText>{pageNum}</PageText>
          </PageButton>
        )
      })}

      <NavButton onClick={handleNextPage} disabled={currentPage >= pageCount}>
        <ChevronRight />
      </NavButton>
    </PaginationContainer>
  )
}
